import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Pencil, Check, X } from 'lucide-react';
import { configurationApi } from '../api/configuration.api';

interface ParametreSysteme {
  idParametre: string;
  code: string;
  nom: string;
  description?: string;
  categorie?: string;
  valeur: string;
  typeValeur: string;
  unite?: string;
  actif: boolean;
  dateDebut: string;
  dateFin?: string;
}

const FORM_VIDE = {
  code: '',
  nom: '',
  description: '',
  categorie: '',
  valeur: '',
  typeValeur: 'DECIMAL',
  unite: '',
  dateDebut: new Date().toISOString().slice(0, 10),
  sourceLegale: '',
};

const TYPE_LABELS: Record<string, string> = {
  DECIMAL: 'Décimal',
  INTEGER: 'Entier',
  STRING: 'Texte',
  BOOLEAN: 'Booléen',
  JSON: 'JSON',
  DATE: 'Date',
  POURCENTAGE: 'Pourcentage',
};

const DESCRIPTIONS_CONNUES: Record<string, string> = {
  NOMBRE_JOURS_BASE_MOIS: 'Nombre de jours servant de base au calcul du pro-rata salarial lors d\'une embauche en cours de mois',
  QR_CODE_URL: 'URL vers laquelle redirige le code QR imprimé sur les bulletins de paie marqués « Payé »',
};

export function ParametresSystemePage() {
  const [parametres, setParametres] = useState<ParametreSysteme[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(FORM_VIDE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // État édition inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValeur, setEditingValeur] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const charger = async () => {
    try {
      const data = await configurationApi.listerParametres();
      setParametres(Array.isArray(data) ? data : (data.donnees ?? []));
    } catch {
      setError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const startEdit = (p: ParametreSysteme) => {
    setEditingId(p.idParametre);
    setEditingValeur(p.valeur);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValeur('');
  };

  const saveEdit = async (idParametre: string) => {
    setSavingEdit(true);
    setError(null);
    try {
      await configurationApi.modifierParametre(idParametre, { valeur: editingValeur });
      setEditingId(null);
      await charger();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erreur lors de la modification');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await configurationApi.creerParametre({
        ...form,
        dateDebut: form.dateDebut || new Date().toISOString().slice(0, 10),
      });
      setForm(FORM_VIDE);
      setShowForm(false);
      await charger();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">Paramètres système</h1>
          <p className="text-sm text-content-subtle mt-1">
            Paramètres de configuration utilisés dans les calculs de paie
          </p>
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setError(null); }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-content-inverted rounded-lg hover:bg-primary-hover text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nouveau paramètre
        </button>
      </div>

      {error && (
        <div className="bg-danger-light border border-danger rounded-lg p-4 text-sm text-danger flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Formulaire de création */}
      {showForm && (
        <div className="bg-surface rounded-xl border border-border p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-content mb-5">Nouveau paramètre système</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Code *</label>
                <input
                  required
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="ex: NOMBRE_JOURS_BASE_MOIS"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Nom *</label>
                <input
                  required
                  value={form.nom}
                  onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Nombre de jours base mois"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Description</label>
              <input
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Valeur *</label>
                <input
                  required
                  value={form.valeur}
                  onChange={e => setForm(f => ({ ...f, valeur: e.target.value }))}
                  placeholder="26"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Type valeur</label>
                <select
                  value={form.typeValeur}
                  onChange={e => setForm(f => ({ ...f, typeValeur: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                >
                  <option value="DECIMAL">Décimal</option>
                  <option value="INTEGER">Entier</option>
                  <option value="STRING">Texte</option>
                  <option value="BOOLEAN">Booléen</option>
                  <option value="POURCENTAGE">Pourcentage</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Unité</label>
                <input
                  value={form.unite}
                  onChange={e => setForm(f => ({ ...f, unite: e.target.value }))}
                  placeholder="jours"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Catégorie</label>
                <input
                  value={form.categorie}
                  onChange={e => setForm(f => ({ ...f, categorie: e.target.value }))}
                  placeholder="CALCUL_PAIE"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-muted mb-1">Date début *</label>
                <input
                  type="date"
                  required
                  value={form.dateDebut}
                  onChange={e => setForm(f => ({ ...f, dateDebut: e.target.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Source légale</label>
              <input
                value={form.sourceLegale}
                onChange={e => setForm(f => ({ ...f, sourceLegale: e.target.value }))}
                placeholder="Décret n°2026-001"
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
              />
            </div>

            <div className="flex gap-3 pt-2 border-t border-border">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary text-content-inverted rounded-lg hover:bg-primary-hover text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? 'Création...' : 'Créer le paramètre'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(FORM_VIDE); setError(null); }}
                className="px-4 py-2 bg-surface-overlay text-content-muted rounded-lg hover:bg-border text-sm font-medium transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tableau des paramètres */}
      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-content-subtle text-sm">Chargement...</div>
        ) : parametres.length === 0 ? (
          <div className="p-10 text-center text-content-subtle">
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-content-subtle" />
            <p className="font-medium">Aucun paramètre système</p>
            <p className="text-xs mt-1 text-content-subtle">Les paramètres sont créés via le seed ou le formulaire ci-dessus</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-raised border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Code / Nom</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Catégorie</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Valeur</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Statut</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {parametres.map(p => (
                <tr key={p.idParametre} className="hover:bg-surface-raised transition-colors">
                  <td className="px-4 py-3 max-w-xs">
                    <p className="font-medium text-content">{p.nom}</p>
                    <p className="text-xs text-content-subtle font-mono mt-0.5">{p.code}</p>
                    {DESCRIPTIONS_CONNUES[p.code] && (
                      <p className="text-xs text-content-subtle mt-1 italic">{DESCRIPTIONS_CONNUES[p.code]}</p>
                    )}
                    {p.description && !DESCRIPTIONS_CONNUES[p.code] && (
                      <p className="text-xs text-content-subtle mt-1">{p.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-content-muted text-xs">
                    {p.categorie ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    {editingId === p.idParametre ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={editingValeur}
                          onChange={e => setEditingValeur(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit(p.idParametre);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          className="w-28 border border-primary rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                        />
                        {p.unite && <span className="text-xs text-content-subtle">{p.unite}</span>}
                        <button
                          onClick={() => saveEdit(p.idParametre)}
                          disabled={savingEdit}
                          className="p-1 text-success hover:bg-success-light rounded transition-colors"
                          title="Confirmer"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-content-subtle hover:bg-surface-overlay rounded transition-colors"
                          title="Annuler"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="font-mono text-content">
                        {p.valeur}{p.unite ? <span className="text-xs text-content-subtle ml-1">{p.unite}</span> : null}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-overlay text-content-muted">
                      {TYPE_LABELS[p.typeValeur] ?? p.typeValeur}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      p.actif
                        ? 'bg-success-light text-success'
                        : 'bg-surface-overlay text-content-subtle'
                    }`}>
                      {p.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingId !== p.idParametre && (
                      <button
                        onClick={() => startEdit(p)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-primary-text hover:bg-primary-light rounded-lg text-xs font-medium transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Modifier
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
